import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import { LeagueSelect } from "@/components/LeagueSelect";
import { TeamSelect } from "@/components/TeamSelect";
import { useAva } from "@/context/AvaContext";
import { Volume2, AlertTriangle, Info, Loader2, Eye, EyeOff, ShieldAlert, Gauge, Radio, RefreshCw, CheckCircle2 } from "lucide-react";
import { PlayerDossier } from "@/components/PlayerDossier";
import { Link, useLocation } from "react-router-dom";

const statusColor = { READY: "#ccff00", MANAGED: "#ffb800", DOUBT: "#ff7300", OUT: "#ff3b30", UNTESTED: "#5b6cff" };
const rColor = (v) => (v >= 75 ? "#ccff00" : v >= 60 ? "#ffb800" : v >= 40 ? "#ff7300" : "#ff3b30");
const mColor = (v) => (v >= 80 ? "#ccff00" : v >= 68 ? "#33d17a" : v >= 52 ? "#00e5ff" : v >= 38 ? "#ffb800" : "#ff3b30");
const riskColor = (v) => (v >= 55 ? "#ff3b30" : v >= 30 ? "#ff7300" : v >= 15 ? "#ffb800" : "#33d17a");
const acwrColor = (r) => (r < 0.8 ? "#00e5ff" : r <= 1.3 ? "#33d17a" : r <= 1.5 ? "#ffb800" : "#ff3b30");

const Bar = ({ value, color }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/8">
    <div className="h-full rounded-full" style={{ width: `${value}%`, background: color, boxShadow: `0 0 8px ${color}` }} />
  </div>
);

const selectCls = "rounded-xl border border-white/10 bg-[#0a0a0a] px-4 py-3 text-sm text-white outline-none focus:border-cyan-400/50";

export default function PlayerHealth() {
  const { speak } = useAva();
  const loc = useLocation();
  const preset = loc.state || {};
  const [leagues, setLeagues] = useState([]);
  const [league, setLeague] = useState(preset.league || 94);   // default to Benfica's league (the coach's club)
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState(preset.team_id || null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [hideUntested, setHideUntested] = useState(false);
  const [live, setLive] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const pendingTeam = useRef(preset.team_id || 211);  // club to select once its league's list loads (default: Benfica)

  const teamName = teams.find((t) => t.id === teamId)?.name || "";

  const loadLive = (refresh = false) => {
    if (!teamId) return;
    setLiveLoading(true);
    api.get(`/players/live-check?team_id=${teamId}&team_name=${encodeURIComponent(teamName)}${refresh ? "&refresh=true" : ""}`)
      .then((r) => setLive(r.data))
      .catch(() => setLive(null))
      .finally(() => setLiveLoading(false));
  };

  useEffect(() => { api.get("/leagues").then((r) => setLeagues(r.data)); }, []);
  useEffect(() => {
    api.get(`/teams?league=${league}`).then((r) => {
      setTeams(r.data);
      const want = pendingTeam.current;
      pendingTeam.current = null;
      setTeamId(want ? (r.data.find((t) => t.id === want)?.id || r.data[0]?.id || null) : (r.data[0]?.id || null));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [league]);
  // Voice can re-open this page for ANY club at any moment (even while it's already open) — apply it live,
  // so nothing is locked to a default team.
  useEffect(() => {
    const st = loc.state || {};
    if (!st.team_id) return;
    pendingTeam.current = st.team_id;
    if (st.league && st.league !== league) setLeague(st.league);  // league effect will then pick the pending club
    else setTeamId(st.team_id);                                    // same league → switch immediately
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.key]);
  useEffect(() => {
    if (!teamId) return;
    setLoading(true);
    setLive(null);
    api.get(`/players?team_id=${teamId}`).then((r) => setPlayers(r.data)).finally(() => setLoading(false));
    loadLive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  // re-pull the squad once the live cross-check resolves, so live OUT/DOUBT overrides show on the cards
  useEffect(() => {
    if (live?.available && teamId) {
      api.get(`/players?team_id=${teamId}`).then((r) => setPlayers(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live?.checked_at]);

  const atRisk = players.filter((p) => ["OUT", "DOUBT"].includes(p.status)).sort((a, b) => a.readiness - b.readiness);
  const avg = players.length ? Math.round(players.reduce((s, p) => s + p.readiness, 0) / players.length) : 0;
  const avgMorale = players.length ? Math.round(players.reduce((s, p) => s + (p.mindset?.morale || 0), 0) / players.length) : 0;
  const avgRes = players.length ? Math.round(players.reduce((s, p) => s + (p.mindset?.resonance?.score || 0), 0) / players.length) : 0;
  const avgConf = players.length ? Math.round(players.reduce((s, p) => s + (p.mindset?.bayes?.confidence || 0) * 100, 0) / players.length) : 0;
  const lowMorale = players.filter((p) => p.mindset && p.mindset.morale < 52).sort((a, b) => a.mindset.morale - b.mindset.morale);
  const untestedCount = players.filter((p) => p.status === "UNTESTED").length;
  const shown = hideUntested ? players.filter((p) => p.status !== "UNTESTED") : players;
  const dangerList = players.filter((p) => p.injury?.danger).sort((a, b) => (b.injury?.pct || 0) - (a.injury?.pct || 0));

  const analyse = () => {
    const names = atRisk.slice(0, 3).map((p) => `${p.name}${p.injury_reason ? ` (${p.injury_reason})` : ` at ${p.readiness}% ready`}`).join(", ");
    const minds = lowMorale.slice(0, 2).map((p) => `${p.name} (${p.mindset.state.toLowerCase()})`).join(", ");
    speak(`Readiness and mindset analysis complete. Squad average readiness ${avg} percent, average morale ${avgMorale} percent, with mean Bayesian confidence ${avgConf} percent. Average Hebbian resonance with my winning profile is ${avgRes} percent. ${atRisk.length} players flagged for fitness. Watch fitness: ${names || "no concerns"}. Watch mindset: ${minds || "morale is healthy across the squad"}. Each morale reading is a Bayesian posterior over a player's latent mind-state, its credible band and precision, recall and accuracy reflect how strongly the evidence agrees.`, { force: true });
  };

  return (
    <div className="px-6 pb-24 pt-20 sm:px-10 lg:px-16" data-octon-fixture={teamId ? String(teamId) : ""} data-octon-fixture-label={teams.find((t) => t.id === Number(teamId))?.name || ""}>
      <p className="font-tac text-[11px] tracking-[0.35em] txt-cyan">SPORTS SCIENCE · REAL DATA</p>
      <h1 className="font-head mt-2 text-5xl font-black uppercase tracking-tighter">Fitness &amp; Mindset</h1>

      {/* explainer */}
      <div className="mt-3 flex items-start gap-2 rounded-xl border border-cyan-400/15 bg-cyan-400/5 px-4 py-3 text-xs text-white/70" data-testid="readiness-explainer">
        <Info size={15} className="mt-0.5 shrink-0 txt-cyan" />
        <p>
          <span className="text-white">Readiness</span> blends real minutes load, age recovery and live injury status.
          <span className="text-white"> Mindset/Morale</span> is a <span className="txt-cyan">Bayesian posterior</span> over each player's latent match-ready mind-state — inferred from form, involvement, freshness, availability and their
          <span className="txt-cyan"> 512-dim Hebbian resonance</span> with OCTON's learned winning profile. Every reading carries a 95% credible band and <span className="txt-cyan">precision / recall / accuracy</span> showing how strongly the evidence agrees.
        </p>
      </div>

      <div className="mt-5 flex flex-wrap items-end gap-3">
        <LeagueSelect testId="health-league-select" value={league} onChange={(id) => setLeague(id)} />
        <TeamSelect testId="health-team-select" teams={teams} value={teamId} onChange={(id) => setTeamId(id)} />
        <button data-testid="analyse-fitness-btn" onClick={analyse}
          className="inline-flex items-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-400/5 px-5 py-3 text-sm text-cyan-200 hover:bg-cyan-400/10">
          <Volume2 size={16} /> OCTON readiness analysis
        </button>
        <button data-testid="toggle-untested-btn" onClick={() => setHideUntested((v) => !v)} disabled={!untestedCount}
          className={`inline-flex items-center gap-2 rounded-xl border px-5 py-3 text-sm transition disabled:opacity-40 ${hideUntested ? "border-[#5b6cff]/60 bg-[#5b6cff]/10 text-[#a6b0ff]" : "border-white/10 text-white/60 hover:text-white"}`}>
          {hideUntested ? <EyeOff size={16} /> : <Eye size={16} />} {hideUntested ? "Show untested" : `Hide untested${untestedCount ? ` (${untestedCount})` : ""}`}
        </button>
        <Link to="/ingest" data-testid="health-ingest-link"
          className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-5 py-3 text-sm text-white/60 transition hover:border-cyan-400/40 hover:text-white">
          <Gauge size={16} /> Ingest load / stats
        </Link>
      </div>
      {hideUntested && untestedCount > 0 && (
        <p className="mt-2 text-[11px] text-[#a6b0ff]/70" data-testid="untested-hidden-note">Hiding {untestedCount} match-unproven player{untestedCount > 1 ? "s" : ""} (0 minutes logged) — showing only match-proven options.</p>
      )}

      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="card-tac p-4"><p className="font-tac text-[9px] tracking-widest text-white/40">AVG READINESS</p><p className="font-head text-4xl font-black" style={{ color: rColor(avg) }}>{avg}%</p></div>
        <div className="card-tac p-4"><p className="font-tac text-[9px] tracking-widest text-white/40">AVG MORALE</p><p className="font-head text-4xl font-black" style={{ color: mColor(avgMorale) }}>{avgMorale}%</p></div>
        <div className="card-tac p-4"><p className="font-tac text-[9px] tracking-widest text-white/40">FITNESS FLAGGED</p><p className="font-head text-4xl font-black txt-red">{atRisk.length}</p></div>
        <div className="card-tac p-4"><p className="font-tac text-[9px] tracking-widest text-white/40">MINDSET FLAGGED</p><p className="font-head text-4xl font-black" style={{ color: "#ffb800" }}>{lowMorale.length}</p></div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-2" data-testid="mindset-science-strip">
        <div className="card-tac flex items-center justify-between p-4">
          <div>
            <p className="font-tac text-[9px] tracking-widest text-white/40">AVG HEBBIAN RESONANCE</p>
            <p className="text-[10px] text-white/45">512-dim alignment with OCTON's winning profile</p>
          </div>
          <p className="font-head text-4xl font-black" style={{ color: mColor(avgRes) }} data-testid="avg-resonance">{avgRes}%</p>
        </div>
        <div className="card-tac flex items-center justify-between p-4">
          <div>
            <p className="font-tac text-[9px] tracking-widest text-white/40">AVG BAYESIAN CONFIDENCE</p>
            <p className="text-[10px] text-white/45">evidence agreement behind the estimates</p>
          </div>
          <p className="font-head text-4xl font-black" style={{ color: mColor(avgConf) }} data-testid="avg-confidence">{avgConf}%</p>
        </div>
      </div>

      {atRisk.length > 0 && (
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-300">
          <AlertTriangle size={16} /> {atRisk.slice(0, 4).map((p) => `${p.name}${p.injury_reason ? ` — ${p.injury_reason}` : ""}`).join(" · ")}
        </div>
      )}

      {/* INJURY RADAR — players spiking into the danger zone (risk% ≥ 55 or ACWR spike > 1.5) */}
      <div className="mt-4 rounded-2xl border border-white/10 bg-black/30 p-4" data-testid="injury-radar" data-octon-section="injury radar danger zone acwr" data-octon-label="Injury Radar">
        <div className="mb-3 flex items-center gap-2">
          <ShieldAlert size={16} style={{ color: dangerList.length ? "#ff3b30" : "#33d17a" }} />
          <span className="font-tac text-[11px] tracking-[0.25em] text-white/80">INJURY RADAR</span>
          <span className="ml-auto font-tac text-[9px] tracking-widest" style={{ color: dangerList.length ? "#ff3b30" : "#33d17a" }} data-testid="radar-count">{dangerList.length} IN DANGER ZONE</span>
        </div>
        {dangerList.length ? (
          <div className="flex flex-wrap gap-2" data-testid="radar-list">
            {dangerList.map((p) => (
              <button key={p.id} onClick={() => setSelected(p)} data-testid={`radar-chip-${p.id}`}
                className="group inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-[12px] text-white/80 transition hover:border-red-500/60 hover:bg-red-500/20">
                <span className="truncate max-w-[130px]">{p.name}</span>
                <span className="font-mono font-bold" style={{ color: riskColor(p.injury.pct) }}>{p.injury.pct}%</span>
                <span className="font-mono text-[10px]" style={{ color: acwrColor(p.acwr.ratio) }} title="ACWR">ACWR {p.acwr.ratio}</span>
              </button>
            ))}
          </div>
        ) : (
          <p className="text-[12px] text-white/45">No player in the danger zone — load, freshness and ACWR are within safe corridors across the squad.</p>
        )}
        <p className="mt-2 text-[10px] text-white/35">Flags fold the live/modelled ACWR ratio straight into the injury-risk score. Tap a name for the full model breakdown.</p>
      </div>

      {/* LIVE INTEL CROSS-CHECK — confirmed XI, injuries & suspensions from current web sources */}
      <div className="mt-4 rounded-2xl border border-cyan-400/20 bg-cyan-400/[0.04] p-4" data-testid="live-intel-panel" data-octon-section="live intel cross-check web news injuries suspensions" data-octon-label="Live Intel Cross-Check">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Radio size={16} className={liveLoading ? "txt-cyan animate-pulse" : "txt-cyan"} />
          <span className="font-tac text-[11px] tracking-[0.25em] text-white/85">LIVE INTEL CROSS-CHECK</span>
          {live?.live?.as_of && <span className="rounded-full bg-white/5 px-2 py-0.5 font-tac text-[9px] tracking-widest text-white/50">AS OF {String(live.live.as_of).toUpperCase()}</span>}
          {live?.live?.confidence && <span className="font-tac text-[9px] tracking-widest" style={{ color: live.live.confidence === "high" ? "#ccff00" : live.live.confidence === "medium" ? "#ffb800" : "#ff7300" }}>{live.live.confidence.toUpperCase()} CONFIDENCE</span>}
          <button data-testid="live-refresh-btn" onClick={() => loadLive(true)} disabled={liveLoading}
            className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 px-3 py-1 text-[11px] text-cyan-200 transition hover:bg-cyan-400/10 disabled:opacity-40">
            {liveLoading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} {liveLoading ? "Checking web…" : "Refresh from web"}
          </button>
        </div>

        {liveLoading && !live ? (
          <p className="flex items-center gap-2 text-[12px] text-white/50"><Loader2 size={14} className="animate-spin txt-cyan" /> Pulling current team news from live web sources…</p>
        ) : !live?.available ? (
          <p className="text-[12px] text-white/45">Live web check unavailable right now — showing the model's read only. Tap “Refresh from web”.</p>
        ) : (
          <div className="space-y-3" data-testid="live-intel-content">
            {live.live.summary && <p className="text-[13px] leading-relaxed text-white/75">{live.live.summary}</p>}

            {live.live.conflicts?.filter((c) => /partial|conflict/i.test(c.model_says || "") || /out|injur|suspend|doubt/i.test(c.web_says || "")).length > 0 && (
              <div className="rounded-xl border border-[#ff7300]/30 bg-[#ff7300]/[0.06] p-3" data-testid="live-conflicts">
                <p className="mb-1.5 font-tac text-[9px] tracking-widest text-[#ffb800]">⚠ WEB vs MODEL — CONFLICTS RECONCILED</p>
                <ul className="space-y-1.5">
                  {live.live.conflicts.map((c, i) => (
                    <li key={i} className="text-[12px] text-white/70">
                      <span className="font-semibold text-white">{c.player}:</span> web says <span className="text-[#ffb800]">{c.web_says}</span> · model had <span className="text-white/45">{c.model_says}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <p className="mb-1 font-tac text-[9px] tracking-widest text-white/40">INJURED (LIVE)</p>
                {live.live.injuries?.length ? (
                  <ul className="space-y-1" data-testid="live-injuries">{live.live.injuries.map((x, i) => <li key={i} className="text-[12px] text-red-300">⚕ {x.name}<span className="text-white/40"> — {x.reason}</span></li>)}</ul>
                ) : <p className="text-[12px] text-white/45">None reported.</p>}
              </div>
              <div>
                <p className="mb-1 font-tac text-[9px] tracking-widest text-white/40">SUSPENDED (LIVE)</p>
                {live.live.suspensions?.length ? (
                  <ul className="space-y-1" data-testid="live-suspensions">{live.live.suspensions.map((x, i) => <li key={i} className="text-[12px] text-[#ffb800]">⛔ {x.name}<span className="text-white/40"> — {x.reason}</span></li>)}</ul>
                ) : <p className="text-[12px] text-white/45">None reported.</p>}
              </div>
              <div>
                <p className="mb-1 font-tac text-[9px] tracking-widest text-white/40">DOUBTS (LIVE)</p>
                {live.live.doubts?.length ? (
                  <ul className="space-y-1" data-testid="live-doubts">{live.live.doubts.map((x, i) => <li key={i} className="text-[12px] text-white/70">? {x.name}<span className="text-white/40"> — {x.reason}</span></li>)}</ul>
                ) : <p className="text-[12px] text-white/45">None reported.</p>}
              </div>
            </div>

            {live.live.confirmed_xi?.length > 0 && (
              <div>
                <p className="mb-1 font-tac text-[9px] tracking-widest text-white/40">EXPECTED / CONFIRMED XI (LIVE WEB)</p>
                <div className="flex flex-wrap gap-1.5" data-testid="live-xi">
                  {live.live.confirmed_xi.map((n, i) => <span key={i} className="rounded-md bg-white/5 px-2 py-1 text-[11px] text-white/70">{n}</span>)}
                </div>
              </div>
            )}
            <p className="flex items-center gap-1.5 text-[10px] text-white/35"><CheckCircle2 size={11} className="txt-cyan" /> {live.source}</p>
          </div>
        )}
      </div>

      {loading ? (
        <div className="mt-6 flex items-center gap-2 text-white/50" data-octon-section="players squad fitness cards readiness" data-octon-label="Player Fitness Cards" data-octon-state="loading"><Loader2 className="animate-spin" size={18} /> Pulling real squad…</div>
      ) : (
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-octon-section="players squad fitness cards readiness" data-octon-label="Player Fitness Cards" data-octon-state="ready">
          {shown.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.015 }}
              onClick={() => setSelected(p)} role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter") setSelected(p); }}
              className="card-tac cursor-pointer overflow-hidden transition hover:border-cyan-400/40 hover:shadow-[0_0_24px_rgba(0,229,255,0.18)]" data-testid={`player-card-${p.id}`}>
              <div className="flex items-center gap-3 p-4">
                <div className="relative shrink-0">
                  <img src={p.photo} alt={p.name} loading="lazy" className="h-14 w-14 rounded-xl object-cover"
                    style={{ boxShadow: `0 0 0 2px ${statusColor[p.status]}66` }} onError={(e) => { e.target.style.opacity = 0.25; }} />
                  <span className="absolute -bottom-1 -right-1 h-3.5 w-3.5 rounded-full border-2 border-[#0a0a0a]"
                    style={{ background: statusColor[p.status], boxShadow: `0 0 8px ${statusColor[p.status]}` }} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-body text-base font-semibold text-white">{p.name}</p>
                  <p className="text-[11px] text-white/40">{p.position_detail}{p.number ? ` · #${p.number}` : ""}{p.age ? ` · ${p.age}y` : ""}</p>
                  <span className="mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-tac text-[8px] tracking-widest"
                    style={{ background: `${statusColor[p.status]}1f`, color: statusColor[p.status] }} data-testid={`readiness-${p.id}`}>
                    {p.status}
                    {p.live_source && <span className="inline-flex items-center gap-0.5 text-[#00e5ff]" title="Corrected from live web sources" data-testid={`live-badge-${p.id}`}><Radio size={8} /> LIVE</span>}
                  </span>
                </div>
                {p.rating
                  ? <div className="text-right"><span className="font-head text-2xl font-black" style={{ color: rColor(p.readiness) }}>{p.rating}</span><span className="block font-tac text-[7px] tracking-widest text-white/30">RATING</span></div>
                  : p.factors?.data === "none"
                    ? <div className="text-right"><span className="font-head text-2xl font-black text-white/30">—</span><span className="block font-tac text-[7px] tracking-widest text-white/30">NO DATA</span></div>
                    : <div className="text-right"><span className="font-head text-2xl font-black" style={{ color: rColor(p.readiness) }}>{p.readiness}%</span><span className="block font-tac text-[7px] tracking-widest text-white/30">READY</span></div>}
              </div>
              <div className="px-4 pb-4">
                {p.factors?.data === "none" ? (
                  <div className="mb-1 flex justify-between text-[10px] text-white/40"><span>READINESS</span><span className="text-white/35">No match data</span></div>
                ) : (
                  <div className="mb-1 flex justify-between text-[10px] text-white/40"><span>READINESS</span><span style={{ color: rColor(p.readiness) }}>{p.readiness}%</span></div>
                )}
                <Bar value={p.factors?.data === "none" ? 0 : p.readiness} color={rColor(p.readiness)} />
                {p.injury && (
                  <div className="mt-2 flex items-center gap-2 text-[10px]" data-testid={`injury-badge-${p.id}`}>
                    <span className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5" style={{ background: `${riskColor(p.injury.pct)}1f`, color: riskColor(p.injury.pct) }}>
                      <ShieldAlert size={10} /> {p.injury.pct}% {p.injury.level}
                    </span>
                    {p.acwr && (
                      <span className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5" style={{ background: `${acwrColor(p.acwr.ratio)}1f`, color: acwrColor(p.acwr.ratio) }} title={`ACWR (${p.acwr.source})`}>
                        <Gauge size={10} /> ACWR {p.acwr.ratio}
                      </span>
                    )}
                    {p.injury.danger && <span className="font-tac text-[8px] tracking-widest text-red-400">⚠ DANGER</span>}
                  </div>
                )}
                {p.factors?.data === "none" && <p className="mt-1 text-[9px] text-white/35">No match minutes logged yet — sharpness unproven</p>}
                {p.mindset && (
                  <div className="mt-3" data-testid={`mindset-${p.id}`}>
                    <div className="mb-1 flex justify-between text-[10px] text-white/40">
                      <span>MINDSET · {p.mindset.state}</span>
                      <span style={{ color: mColor(p.mindset.morale) }}>{p.mindset.morale}%</span>
                    </div>
                    {/* morale bar with 95% credible band overlay */}
                    <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-white/8">
                      {p.mindset.band && (
                        <div className="absolute top-0 h-full bg-white/12"
                          style={{ left: `${p.mindset.band.low}%`, width: `${Math.max(2, p.mindset.band.high - p.mindset.band.low)}%` }} />
                      )}
                      <div className="absolute top-0 h-full rounded-full" style={{ width: `${p.mindset.morale}%`, background: mColor(p.mindset.morale), boxShadow: `0 0 8px ${mColor(p.mindset.morale)}` }} />
                    </div>
                    {p.mindset.band && (
                      <p className="mt-1 text-[9px] font-mono text-white/35" data-testid={`ci-${p.id}`}>95% CI {p.mindset.band.low}–{p.mindset.band.high}% · σ{p.mindset.band.std}</p>
                    )}
                    <p className="mt-1 text-[10px] leading-snug text-white/45">{p.mindset.drivers.join(" · ")}</p>

                    {/* Hebbian resonance */}
                    {p.mindset.resonance && (
                      <div className="mt-2" data-testid={`resonance-${p.id}`}>
                        <div className="mb-1 flex justify-between text-[9px] text-white/40">
                          <span>HEBBIAN RESONANCE · fits {p.mindset.resonance.aligned_formation}</span>
                          <span style={{ color: mColor(p.mindset.resonance.score) }}>{p.mindset.resonance.score}%</span>
                        </div>
                        <Bar value={p.mindset.resonance.score} color={mColor(p.mindset.resonance.score)} />
                      </div>
                    )}

                    {/* Bayesian reliability */}
                    {p.mindset.bayes && (
                      <div className="mt-2 grid grid-cols-4 gap-1 font-mono text-[9px] text-white/55" data-testid={`bayes-${p.id}`}>
                        <div><span className="block text-white/30">PREC</span>{Math.round(p.mindset.bayes.precision * 100)}%</div>
                        <div><span className="block text-white/30">RECALL</span>{Math.round(p.mindset.bayes.recall * 100)}%</div>
                        <div><span className="block text-white/30">ACC</span>{Math.round(p.mindset.bayes.accuracy * 100)}%</div>
                        <div><span className="block text-white/30">CONF</span>{Math.round(p.mindset.bayes.confidence * 100)}%</div>
                      </div>
                    )}
                  </div>
                )}
                {p.injury_reason ? (
                  <p className="mt-2 text-[11px] text-red-300">⚕ {p.injury_reason}</p>
                ) : (
                  <div className="mt-2 grid grid-cols-3 gap-2 font-mono text-[10px] text-white/50">
                    <div><span className="block text-white/30">MINUTES</span>{p.factors.minutes}</div>
                    <div><span className="block text-white/30">APPS</span>{p.factors.apps}</div>
                    <div><span className="block text-white/30">LOAD</span>{p.factors.load}%</div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {selected && <PlayerDossier teamId={teamId} player={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
